import React, { useState } from 'react';
import { Card, Row, Col, Typography, Form, Input, Button, Select, Space, message, Divider } from 'antd';
import styles from '../components/Layout/AboutPage.module.css';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ContactPage = () => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFinish = async (values) => {
    setIsSubmitting(true);
    // TODO: Wire this up to a real backend endpoint when available
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    message.success('Thank you for reaching out! We will get back to you shortly.');
    form.resetFields();
    setIsSubmitting(false);
  };

  const handleFinishFailed = () => {
    message.error('Please fix the validation errors in the form before submitting.');
  };

  return (
    <div className={styles.container}>
      {/* Main Contact Section */}
      <Card 
        className={styles.sectionCard}
        style={{
          borderRadius: 24,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          overflow: 'hidden'
        }}
      >
        <Row gutter={[48, 48]}>
          {/* Left Column - Information */}
          <Col xs={24} lg={10}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <div style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 24
                }}>
                  
                </div>
                <Title level={2} style={{ marginBottom: 16, fontSize: 'clamp(1.5rem, 5vw, 2rem)' }}>
                  Get in Touch
                </Title>
                <Paragraph style={{ color: '#64748b', fontSize: 16, lineHeight: 1.7, marginBottom: 24 }}>
                  Whether you're evaluating Observability Accelerator, planning a rollout, or need help
                  with an existing setup, we're here to help you design the right solution for your AWS
                  environment.
                </Paragraph>
                <Paragraph style={{ color: '#64748b', fontSize: 16, lineHeight: 1.7 }}>
                  Share details about your use case and current tooling. We'll review your message and
                  connect you with a specialist who can provide concrete next steps.
                </Paragraph>
              </div>

              <Divider style={{ margin: '24px 0' }} />

              <div>
                <Title level={5} style={{ marginBottom: 20 }}>Why reach out?</Title>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {[
                    { text: 'Global, remote-first team ready to assist', color: '#1677ff' },
                    { text: 'Enterprise support and dedicated onboarding', color: '#52c41a' },
                    { text: 'Typical response: 1-2 business days', color: '#faad14' },
                  ].map((item, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 16px',
                      background: `${item.color}08`,
                      borderRadius: 12,
                      borderLeft: `3px solid ${item.color}`
                    }}>
                      <Text style={{ fontSize: 15 }}>{item.text}</Text>
                    </div>
                  ))}
                </Space>
              </div>
            </Space>
          </Col>

          {/* Right Column - Form */}
          <Col xs={24} lg={14}>
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 20,
              padding: 'clamp(2rem, 5vw, 3rem)',
              boxShadow: '0 12px 40px rgba(102, 126, 234, 0.25)'
            }}>
              <Title 
                level={3} 
                style={{ 
                  color: 'white', 
                  marginBottom: 24,
                  fontSize: 'clamp(1.25rem, 4vw, 1.75rem)'
                }}
              >
                Send us a message
              </Title>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                onFinishFailed={handleFinishFailed}
                requiredMark={false}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>Full Name</span>}
                      name="name"
                      rules={[
                        { required: true, message: 'Please enter your name' },
                        { min: 2, message: 'Name is too short' }
                      ]}
                    >
                      <Input 
                        placeholder="Jane Doe" 
                        autoComplete="name"
                        size="large"
                        style={{ borderRadius: 12 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>Work Email</span>}
                      name="email"
                      rules={[
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email address' }
                      ]}
                    >
                      <Input 
                        placeholder="you@company.com" 
                        autoComplete="email"
                        size="large"
                        style={{ borderRadius: 12 }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>Company</span>}
                      name="company"
                      rules={[
                        { required: true, message: 'Please enter your company name' }
                      ]}
                    >
                      <Input 
                        placeholder="Your organization" 
                        autoComplete="organization"
                        size="large"
                        style={{ borderRadius: 12 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>Topic</span>}
                      name="topic"
                      rules={[{ required: true, message: 'Please select a topic' }]}
                    >
                      <Select 
                        placeholder="Choose a topic"
                        size="large"
                        style={{ borderRadius: 12 }}
                      >
                        <Option value="demo">Product demo</Option>
                        <Option value="onboarding">Onboarding & setup</Option>
                        <Option value="support">Support & troubleshooting</Option>
                        <Option value="pricing">Pricing & licensing</Option>
                        <Option value="partnership">Partnership opportunities</Option>
                        <Option value="other">Other</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label={<span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>How can we help?</span>}
                  name="message"
                  rules={[
                    { required: true, message: 'Please describe your request' },
                    { min: 10, message: 'Please provide a bit more detail' }
                  ]}
                >
                  <TextArea
                    rows={5}
                    placeholder="Share as much context as possible so we can help quickly..."
                    size="large"
                    style={{ borderRadius: 12 }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                  <Button
                    type="primary"
                    htmlType="submit"size="large"
                    loading={isSubmitting}
                    block
                    style={{
                      height: 50,
                      borderRadius: 12,
                      fontSize: 16,
                      fontWeight: 600,
                      background: 'white',
                      color: '#667eea',
                      border: 'none',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
                    }}
                  >
                    Send Message
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Bottom Info Section */}
      <Card
        style={{
          marginTop: 'clamp(2rem, 5vw, 3rem)',
          borderRadius: 20,
          border: 'none',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}
        bodyStyle={{ padding: 'clamp(2rem, 5vw, 3rem)' }}
      >
        <Row gutter={[32, 32]} align="middle">
          <Col xs={24} md={12}>
            <Title level={3} style={{ marginBottom: 16 }}>
              Prefer a different way to connect?
            </Title>
            <Paragraph style={{ fontSize: 16, color: '#64748b', marginBottom: 24 }}>
              We're flexible! If email works better for you, or if you'd like to schedule a call,
              we're happy to accommodate your preferred communication method.
            </Paragraph>
            <Space size="middle" wrap>
              <Button
                type="default"
                size="large"href="mailto:support@example.com"
                style={{
                  borderRadius: 12,
                  height: 44,
                  paddingLeft: 24,
                  paddingRight: 24,
                  fontWeight: 500
                }}
              >
                Email Us Directly
              </Button>
              <Button
                type="default"
                size="large"style={{
                  borderRadius: 12,
                  height: 44,
                  paddingLeft: 24,
                  paddingRight: 24,
                  fontWeight: 500
                }}
              >
                Schedule a Call
              </Button>
            </Space>
          </Col>
          <Col xs={24} md={12} style={{ textAlign: 'center' }}>
            <div style={{
              background: 'white',
              borderRadius: 16,
              padding: 32,
              boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
            }}>
              
              <Title level={4} style={{ marginBottom: 8 }}>Need Immediate Help?</Title>
              <Text type="secondary" style={{ fontSize: 15 }}>
                Check out our documentation and FAQ for instant answers to common questions.
              </Text>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default React.memo(ContactPage);
